import { useCallback, useRef } from 'react';
import { useLazyQuery } from 'react-apollo';
import gql from 'graphql-tag';

import { useContentTreeSelectionActions } from './store';
import type { ContentId } from './contentTreeItemType';

const ContentMetadataQuery = gql`
	query useSelectionCountContentMetadataQuery($contentIds: [ID!]!) {
		pageContent: content(ids: $contentIds, type: "page") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		whiteboardContent: content(ids: $contentIds, type: "whiteboard") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		databaseContent: content(ids: $contentIds, type: "database") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		embedContent: content(ids: $contentIds, type: "embed") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		folderContent: content(ids: $contentIds, type: "folder") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
	}
`;

export const useSelectionCount = (contentId: ContentId, ancestorIds: ContentId[]) => {
	const {
		updateSelectedDescendantsCount,
		updateTotalCount,
		addLoadingDescendant,
		removeLoadingDescendant,
		incrementAncestors,
	} = useContentTreeSelectionActions();

	const isExplicitlySelectedRef = useRef(false);
	const countToRemoveRef = useRef<number>(0);

	const [fetchMetadata] = useLazyQuery(ContentMetadataQuery, {
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			const nodes = [
				...(data?.pageContent?.nodes ?? []),
				...(data?.whiteboardContent?.nodes ?? []),
				...(data?.databaseContent?.nodes ?? []),
				...(data?.embedContent?.nodes ?? []),
				...(data?.folderContent?.nodes ?? []),
			];

			const isExplicitlySelected = isExplicitlySelectedRef.current;
			const countToRemove = countToRemoveRef.current;
			let selectedCount = 0;

			nodes.forEach((node) => {
				if (node.id === contentId) {
					selectedCount = node.visibleDescendantsCount;
				}
				updateSelectedDescendantsCount(node.id, node.visibleDescendantsCount);
				removeLoadingDescendant(node.id);
			});

			// Only update counts if content is explicitly selected.
			if (isExplicitlySelected) {
				updateTotalCount(selectedCount + 1 - countToRemove); // +1 for the content itself
				incrementAncestors([...ancestorIds], selectedCount, countToRemove);
			} else {
				if (countToRemove > 0) {
					// Remove count that have been previously addressed.
					updateTotalCount(-countToRemove);
				}
			}
		},
		onError: () => {},
	});

	const updateDescendantCount = useCallback(
		(contentForCount: string[], isExplicitlySelected: boolean, countToRemove: number) => {
			isExplicitlySelectedRef.current = isExplicitlySelected;
			countToRemoveRef.current = countToRemove;
			contentForCount.forEach((id) => addLoadingDescendant(id));
			fetchMetadata({ variables: { contentIds: contentForCount } });
		},
		[addLoadingDescendant, fetchMetadata],
	);

	return updateDescendantCount;
};
